import { Outlet, useLocation, useSearchParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { ColumnDef, createColumnHelper, PaginationState, SortingState } from '@tanstack/react-table';
import { toast } from 'react-toastify';
import Portal from 'src/components/layouts/Portal/Portal';
import useFilters from '../../../services/filters';
import Checkbox from '../Checkbox/Checkbox';
import Icon, { IconName } from '../Icon/Icon';
import ActionMenuButton from '../ActionMenuButton/ActionMenuButton';
import Button, { ButtonToneProps } from '../Button/Button';
import { Container } from '../../layouts/Container/Container';
import HeadlineWithButtons from '../HeadlineWithButtons/HeadlineWithButtons';
import { KpiRow } from '../../layouts/KpiRow.tsx/KpiRow';
import styles from '../../../pages/styles/Inventory.module.scss';
import TabBar from '../../layouts/TabBar/TabBar';
import FilterRow from '../../layouts/FilterRow/FilterRow';
import { EntityTable } from '../../tables/InboundTable/InboundTable';
import ConfirmationModal from '../../modals/ConfirmationModal/ConfirmationModal';
import { OptionItemProps } from '../OptionItem/OptionItem';
import { KpiModel } from '../../../services/kpi';
import { combineDomains } from '../../../utils/domain';

export interface PageResult<T> {
  items: T[];
  total?: number | null;
  page: number | null;
  size: number | null;
  pages?: number | null;
}

export interface UsePagedQuery<T> {
  (params: {
    page: number;
    size: number;
    sort_by: string | undefined;
    sort_order: 'asc' | 'desc' | undefined;
    domain: string | undefined;
    tabIndex?: number;
    enabled?: boolean;
    onSuccess?: () => void;
  }): {
    data?: PageResult<T>;
    isLoading: boolean;
  };
}

export interface ConfirmSettings<T> {
  renderTitle: (selectedEntities: T[]) => React.ReactNode;
  renderContent: (selectedEntities: T[]) => React.ReactNode;
  confirmLabel: string;
  cancelLabel: string;
}

export interface TableOverviewAction<T> {
  title: string;
  titlePlural?: string;
  icon?: IconName;
  bulkSupport?: boolean;
  showInContextMenu?: boolean;
  showInToolbar?: boolean;
  toolbarTone?: ButtonToneProps;
  bulkMenuTone?: ButtonToneProps;
  onExecute?: (selected: T[]) => Promise<void> | void;
  isRowClickAction?: boolean;
  isDisabled?: (selected: T[]) => boolean;
  confirmSettings?: ConfirmSettings<T>;
  isError?: boolean;
  toast?: {
    renderSuccessMessage: (selected: T[]) => string;
    renderErrorMessage: (selected: T[]) => string;
  };
  menuItems?: OptionItemProps[];
}

export interface TableOverviewProps<T> {
  filterModelId: string;
  headline?: string;
  kpiModel?: KpiModel;
  columns: ColumnDef<T, any>[];
  useDataQuery: UsePagedQuery<T>;
  tabs?: { label: string; key: string }[];
  getDomainForTab?: (tabKey: string, tabIndex: number) => string;
  actions: TableOverviewAction<T>[];
  withSaveFilters?: boolean;
  sortByTransform?: (sortBy?: string) => string | undefined;
  applyContainer?: boolean;
  displayFilterRow?: boolean;
  displayActionsColumn?: boolean;
  displayColumnSelect?: boolean;
  noResultsLabel?: string;
  allowBulkActions?: boolean;
  fileInput?: JSX.Element;
  /** Odoo domain string that is always ANDed with the tab and filter domain. */
  extraDomain?: string;
}

export function TableOverview<T>({
  filterModelId,
  headline,
  kpiModel,
  columns,
  useDataQuery,
  tabs,
  getDomainForTab,
  actions,
  sortByTransform = (sortBy?: string) => sortBy,
  withSaveFilters = false,
  applyContainer = true,
  displayFilterRow = true,
  displayActionsColumn = true,
  displayColumnSelect = true,
  noResultsLabel,
  allowBulkActions = true,
  fileInput,
  extraDomain,
}: TableOverviewProps<T>) {
  const [domainReady, setDomainReady] = useState(false);
  const [appliedDomain, setAppliedDomain] = useState<string | undefined>(undefined);

  const location = useLocation();
  const preselectedDomain = location.state?.preselectedDomain;
  const preselectedTab = location.state?.preselectedTab;
  const [, setSearchParams] = useSearchParams();

  const filterInstance = useFilters(filterModelId);

  const [activeIndex, setActiveIndex] = useState<number | null>(0);
  const supportsBulk = actions.some(action => action.bulkSupport) && allowBulkActions;
  const [selectedRows, setSelectedRows] = useState<T[]>([]);
  const [rowSelection, setRowSelection] = useState<any>({});

  const [visibleConfirmDialogIndex, setVisibleConfirmDialogIndex] = useState<number | null>(null);
  const [toConfirmEntities, setToConfirmEntities] = useState<T[] | null>();

  const visibleConfirmDialogSettings =
    visibleConfirmDialogIndex === null ? null : actions[visibleConfirmDialogIndex].confirmSettings;

  useEffect(() => {
    if (!domainReady) {
      if (preselectedDomain) {
        filterInstance.setFilter(preselectedDomain);
        filterInstance.setDomain(preselectedDomain);
        setAppliedDomain(preselectedDomain);
      }

      if (preselectedTab && tabs) {
        const idx = tabs.findIndex(t => t.key === preselectedTab);
        if (idx >= 0) setActiveIndex(idx);
      }
      setDomainReady(true);
      setSearchParams({ tab: preselectedTab || '' }, { replace: true });
    }
  }, [preselectedDomain, preselectedTab, tabs, domainReady]);

  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 20 });
  const [sorting, setSorting] = useState<SortingState>([]);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const sortEntry = sorting[0];
  const page = pagination.pageIndex + 1;
  const size = pagination.pageSize;
  const sortBy = sortEntry?.id;
  const sortOrder: 'asc' | 'desc' = sortEntry?.desc ? 'desc' : 'asc';
  const { domain } = filterInstance;
  const tabIndex = activeIndex ?? undefined;

  const effectiveDomain = appliedDomain ?? filterInstance.domain;
  const combinedDomain = combineDomains(extraDomain, effectiveDomain);

  const isReady = appliedDomain !== null || (domainReady && domain !== undefined && domain !== null);

  useEffect(() => {
    setPagination(current => (current.pageIndex === 0 ? current : { ...current, pageIndex: 0 }));
  }, [combinedDomain]);

  const { data, isLoading } = useDataQuery({
    page,
    size,
    sort_by: sortByTransform(sortBy),
    sort_order: sortOrder,
    domain: combinedDomain,
    tabIndex,
    enabled: isReady,
  });

  const executeActionAndShowToast = async (action: TableOverviewAction<T> | undefined, selected: T[]) => {
    try {
      await action?.onExecute?.(selected);
      const successMessage = action?.toast?.renderSuccessMessage?.(selected);
      if (successMessage) {
        toast.success(successMessage);
      }
      if (!successMessage && action?.title.includes('abbrechen')) {
        toast.success(`Aktion: '${action?.title}' war erfolgreich!`);
      }
    } catch (error: any) {
      const errorMessage = action?.toast?.renderErrorMessage?.(selected);
      if (errorMessage) {
        toast.error(errorMessage);
      }
      if (!errorMessage) {
        toast.error(error?.detail);
      }
    }
  };

  const columnHelper = createColumnHelper<T>();

  const bulkColumn = columnHelper.accessor((row: any) => row.name, {
    id: 'bulk',
    size: 30,
    enableHiding: false,
    header: ({ table }) => (
      <div className="flex items-center gap-x-12">
        <div role="none" onClick={e => e.stopPropagation()}>
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        </div>
      </div>
    ),
    cell: info => (
      <div className="flex items-center gap-x-12">
        <div role="none" onClick={e => e.stopPropagation()}>
          <Checkbox
            checked={info.row.getIsSelected()}
            indeterminate={info.row.getIsSomeSelected()}
            onChange={info.row.getToggleSelectedHandler()}
          />
        </div>
      </div>
    ),
  });

  const actionsColumn = columnHelper.display({
    id: 'actions',
    size: 60,
    enableHiding: false,
    enableResizing: false,
    header: () => <Icon className="text-full opacity-60 flex justify-center w-full" name="edit" />,
    cell: ({ row }) => {
      const rowData = row.original;

      return (
        <div className="flex justify-center">
          <ActionMenuButton
            menuItems={actions
              .map((action, actionIndex) => {
                if (!action.showInContextMenu) {
                  return null;
                }
                return {
                  accessoryItem: action.icon,
                  isError: action.isError,
                  label: action.title,
                  disabled: action.isDisabled && action.isDisabled([rowData]),
                  onClick: async (event: any) => {
                    event.stopPropagation();
                    if (action.confirmSettings) {
                      setVisibleConfirmDialogIndex(actionIndex);
                      setToConfirmEntities([rowData]);
                    } else {
                      executeActionAndShowToast(action, [rowData]);
                    }
                  },
                };
              })
              .filter(a => a !== null)}
          />
        </div>
      );
    },
  });

  const finalColumns = (() => {
    const baseColumns = supportsBulk ? [bulkColumn, ...columns] : [...columns];
    return displayActionsColumn ? [...baseColumns, actionsColumn] : baseColumns;
  })();

  const toolbarButtons = actions.map((action, actionIndex) => {
    if (!action.showInToolbar) {
      return null;
    }
    return {
      title: selectedRows.length > 1 && action.titlePlural ? action.titlePlural : action.title,
      tone: action.toolbarTone || ButtonToneProps.PRIMARY_BLUE,
      icon: action.icon,
      menuItems: action.menuItems ?? [],
      onClick: async () => {
        const selected = supportsBulk ? selectedRows : [];
        if (action.confirmSettings) {
          setVisibleConfirmDialogIndex(actionIndex);
          setToConfirmEntities(selected);
        } else {
          try {
            await executeActionAndShowToast(action, selected);
            setSelectedRows([]);
            setRowSelection({});
          } catch (e) {
            console.error(e);
          }
        }
      },
    };
  });

  const addContainer = (
    condition: boolean,
    wrapper: (children: React.ReactNode) => JSX.Element,
    children: React.ReactNode,
  ) => {
    return condition ? wrapper(children) : children;
  };

  return addContainer(
    applyContainer,
    children => <Container>{children}</Container>,
    <>
      {headline && <HeadlineWithButtons headline={headline} buttons={toolbarButtons.filter(b => b !== null)} />}
      {fileInput}
      {kpiModel && <KpiRow model={kpiModel} />}
      <div className={styles.tableContent}>
        <div className={`${tabs || displayFilterRow ? 'mt-24 mb-56' : ''}`}>
          {tabs && (
            <TabBar
              className="mt-40 mb-24"
              tabs={tabs.map((tab, index) => {
                return {
                  ...tab,
                  onClick: () => {
                    setActiveIndex(index);
                    setSearchParams({ tab: tab.key });
                    if (getDomainForTab) {
                      const newDomain = getDomainForTab(tab.key, index);
                      setAppliedDomain(newDomain);
                      filterInstance.setFilter(null);
                      filterInstance.setDomain(newDomain);
                    }
                  },
                };
              })}
              defaultIndex={activeIndex || 0}
            />
          )}
          {displayFilterRow && (
            <FilterRow
              multipleParams
              withSaveFilters={withSaveFilters}
              filterInstance={filterInstance}
              primaryKeys={[
                { key: 'custom', operator: '=' },
                { key: 'date_planned', operator: '<' },
              ]}
            />
          )}
        </div>
        <EntityTable<T>
          isLoading={isLoading}
          containerRef={containerRef}
          data={data?.items || []}
          pages={data?.pages ?? 0}
          pagination={pagination}
          sorting={sorting}
          onSortingChange={setSorting}
          onPaginationChange={setPagination}
          columns={finalColumns}
          onTableRowClick={row => {
            const action = actions.find(action => action.isRowClickAction);
            executeActionAndShowToast(action, [row]);
          }}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          onSelectedRowsChange={setSelectedRows}
          displayColumnSelect={displayColumnSelect}
          noResultsLabel={noResultsLabel}
          getRowId={row => JSON.stringify(row)}
        />
        <ConfirmationModal
          isOpen={Boolean(visibleConfirmDialogSettings)}
          title={visibleConfirmDialogSettings?.renderTitle(toConfirmEntities!) ?? ''}
          description={visibleConfirmDialogSettings?.renderContent(toConfirmEntities!) ?? ''}
          onClose={async confirmed => {
            if (visibleConfirmDialogIndex === null || toConfirmEntities === null) {
              throw new Error(
                `Confirm handling failed because visibleConfirmDialogIndex is ${visibleConfirmDialogIndex} and toConfirmEntities is ${toConfirmEntities}`,
              );
            }
            if (confirmed) {
              const action = actions[visibleConfirmDialogIndex!];
              if (action) {
                try {
                  await executeActionAndShowToast(action, toConfirmEntities!);
                  setSelectedRows([]);
                  setRowSelection({});
                  setVisibleConfirmDialogIndex(null);
                  setToConfirmEntities(null);
                } catch (e) {
                  console.error(e);
                }
              }
            } else {
              setVisibleConfirmDialogIndex(null);
              setToConfirmEntities(null);
            }
          }}
        />
        {selectedRows.length > 0 && (
          <Portal>
            <div className={styles.quickActionMenu}>
              <div className={styles.inner}>
                {actions.map((action, actionIndex) => {
                  if (!action.bulkSupport) {
                    return null;
                  }
                  return (
                    <Button
                      key={action.title}
                      title={selectedRows.length > 1 && action.titlePlural ? action.titlePlural : action.title}
                      tone={action.bulkMenuTone || ButtonToneProps.SECONDARY}
                      icon={action.icon}
                      disabled={action.isDisabled && action.isDisabled(selectedRows)}
                      onClick={async () => {
                        if (action.confirmSettings) {
                          setVisibleConfirmDialogIndex(actionIndex);
                          setToConfirmEntities(selectedRows);
                        } else {
                          try {
                            await executeActionAndShowToast(action, selectedRows);
                            setSelectedRows([]);
                            setRowSelection({});
                          } catch (e) {
                            console.error(e);
                          }
                        }
                      }}
                    />
                  );
                })}
              </div>
            </div>
          </Portal>
        )}
      </div>
      <Outlet />
    </>,
  );
}
